import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

/**
 * 权限管理组合式函数
 * 用于在组件中检查权限和控制UI显示
 */
export function usePermission() {
  const authStore = useAuthStore()

  /**
   * 检查单个权限
   * @param permissionCode 权限代码
   * @returns 是否有权限
   */
  const hasPermission = (permissionCode: string): boolean => {
    return authStore.hasPermission(permissionCode)
  }

  /**
   * 检查权限是否已显式分配到当前租户菜单
   * 不因租户管理员角色而绕过。
   */
  const hasExplicitMenuPermission = (permissionCode: string): boolean => {
    return authStore.hasExplicitMenuPermission(permissionCode)
  }

  /**
   * 检查多个权限（任一满足）
   * @param permissionCodes 权限代码数组
   * @returns 是否有任一权限
   */
  const hasAnyPermission = (permissionCodes: string[]): boolean => {
    return permissionCodes.some(code => hasPermission(code))
  }

  /**
   * 检查多个权限（全部满足）
   * @param permissionCodes 权限代码数组
   * @returns 是否有所有权限
   */
  const hasAllPermissions = (permissionCodes: string[]): boolean => {
    return permissionCodes.every(code => hasPermission(code))
  }

  /**
   * 获取用户所有权限代码
   */
  const permissionCodes = computed(() => {
    const menuCodes = authStore.userPermissions
      .filter(p => p.status === 1)
      .map(p => p.permissionCode)
    return [...new Set([...menuCodes, ...authStore.rolePermissionCodes])]
  })

  /**
   * 获取权限加载状态
   */
  const permissionsLoaded = computed(() => authStore.permissionsLoaded)

  /**
   * 检查是否为管理员
   */
  const isAdmin = computed(() => authStore.isAdmin)

  /**
   * 检查权限或管理员
   * @param permissionCode 权限代码
   * @returns 是否有权限或为管理员
   */
  const hasPermissionOrAdmin = (permissionCode: string): boolean => {
    return isAdmin.value || hasPermission(permissionCode)
  }

  return {
    permissions: authStore.userPermissions,
    permissionCodes,
    permissionsLoaded,
    isAdmin,
    hasPermission,
    hasExplicitMenuPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasPermissionOrAdmin
  }
}

/**
 * 权限路由元信息类型扩展
 */
declare module 'vue-router' {
  interface RouteMeta {
    /** 路由所需权限 */
    permission?: string
    /** 路由标题 */
    title?: string
    /** 路由标题英文（来自后端权限 nameEn） */
    titleEn?: string
    /** 图标 */
    icon?: string
    /** 是否隐藏 */
    hidden?: boolean
    /** 排序 */
    order?: number
    /** 是否为菜单组 */
    isMenuGroup?: boolean
    /** 徽章 */
    badge?: string
    /** 是否需要认证 */
    requiresAuth?: boolean
    /** 激活的菜单项 */
    activeMenu?: string
  }
}
